/**
 * The Game tab's staged picks guard, copied from `PickSheetContainer.tsx`
 * (lines 293-348 plus the ref assignment at 498). The tab stays mounted
 * across pool tabs (`keepMounted`), so only leaving the pool screen,
 * switching pools or unloading the page can drop the picks; a round or
 * entry switch inside the tab asks through `confirmDiscardIfDirty` instead
 * of this guard.
 */
import { confirmDestructive } from "@sportsmet/ui";
import { useBlocker } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

import {
  PICK_DISCARD_BODY,
  PICK_DISCARD_CANCEL_LABEL,
  PICK_DISCARD_CONFIRM_LABEL,
  PICK_DISCARD_TITLE,
} from "../pick/pickCopy";
import {
  readSearchString,
  shouldBlockPickSheetLeave,
} from "../pick/pickSheetLeaveGuard";

export function useSurvivorLeaveGuard(input: {
  isDirty: boolean;
  onDiscard: () => void;
}): void {
  const isDirtyRef = useRef(false);
  isDirtyRef.current = input.isDirty;

  const onDiscardRef = useRef(input.onDiscard);
  onDiscardRef.current = input.onDiscard;

  const shouldBlockFn = useCallback(
    async (args: {
      current: { pathname: string; search: unknown };
      next: { pathname: string; search: unknown };
    }) => {
      if (
        !(
          isDirtyRef.current &&
          readSearchString(args.current.search, "contestId") !==
            readSearchString(args.next.search, "contestId")
        ) &&
        !shouldBlockPickSheetLeave({
          isDirty: isDirtyRef.current,
          currentPathname: args.current.pathname,
          nextPathname: args.next.pathname,
          currentPoolId: readSearchString(args.current.search, "poolId"),
          nextPoolId: readSearchString(args.next.search, "poolId"),
          currentView: readSearchString(args.current.search, "view"),
          nextView: readSearchString(args.next.search, "view"),
        })
      ) {
        return false;
      }
      const isConfirmed = await confirmDestructive({
        title: PICK_DISCARD_TITLE,
        body: PICK_DISCARD_BODY,
        confirmLabel: PICK_DISCARD_CONFIRM_LABEL,
        cancelLabel: PICK_DISCARD_CANCEL_LABEL,
      });
      if (isConfirmed) {
        onDiscardRef.current();
        return false;
      }
      return true;
    },
    [],
  );
  useBlocker({ shouldBlockFn, enableBeforeUnload: false, withResolver: false });

  useEffect(() => {
    if (!input.isDirty) return;
    function onBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      // Required for cross-browser beforeunload warnings; preventDefault alone is not enough.
      // oxlint-disable-next-line typescript/no-deprecated -- ISSUE-973 leave guard, as in PickSheetContainer
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [input.isDirty]);
}
